import { useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router';
import { useSession } from '@london-life/auth-client';
import type { AdminSessionReply } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { SignInCard } from '@london-life/ui/auth/sign-in-card';
import { AdminShell } from '@london-life/ui/admin/admin-shell';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api, browserAuth, csrf, sessionEnded } from './lib/api';
import { env } from './lib/env';
import { AdminContext } from './lib/admin-context';
import { GROUPS, crumbsFor, pagesFor } from './navigation';
import { TwoStepPrompt } from './two-step/two-step-prompt';
import { TwoStepSetup } from './two-step/two-step-setup';
import { TwoStepVerify } from './two-step/two-step-verify';

// sign in, load the admin's permissions once, then show the dashboard with only the sections they may use
export function App() {
  const session = useSession(api, browserAuth, 'admin', csrf);
  const [admin, setAdmin] = useState<AdminSessionReply | null | { denied: FriendlyError } | FriendlyError>(null);
  const [attempt, setAttempt] = useState(0);
  const [notice, setNotice] = useState<string | undefined>();
  // a request found the session ended: check again (which shows the sign-in card) and say why
  const { check } = session;
  useEffect(() => {
    const ended = (event: Event) => {
      setNotice((event as CustomEvent<string>).detail || 'Your session has ended. Please sign in again.');
      check();
    };
    sessionEnded.addEventListener('ended', ended);
    return () => sessionEnded.removeEventListener('ended', ended);
  }, [check]);
  useEffect(() => {
    if (session.state.status !== 'signed-in') {
      setAdmin(null);
      return;
    }
    // only a 401 or 403 means "not an admin"; a network or server failure is shown with Try again
    api.admin
      .me()
      .then(setAdmin)
      .catch((problem: unknown) => {
        const failure = describeError(problem);
        setAdmin(failure.kind === 'unauthorized' || failure.kind === 'forbidden' ? { denied: failure } : failure);
      });
  }, [session.state.status, attempt]);

  if (session.state.status === 'loading') return <p className="admin-centre">Loading…</p>;
  if (session.state.status === 'unavailable')
    return (
      <div className="admin-centre">
        <ErrorNotice error={session.state.error} onRetry={session.check} />
      </div>
    );
  if (session.state.status === 'guest')
    return (
      <div className="admin-centre">
        <SignInCard
          title="London Life Admin"
          notice={notice}
          onGoogle={session.signInWithGoogle}
          onEmailSignIn={session.signInWithEmail}
          onReset={session.sendPasswordReset}
        />
      </div>
    );
  if (admin === null) return <p className="admin-centre">Checking your access…</p>;
  if ('denied' in admin)
    return (
      <div className="admin-centre">
        <p>{admin.denied.message}</p>
        <button type="button" onClick={() => void session.signOut()}>
          Sign out
        </button>
      </div>
    );
  if ('kind' in admin)
    return (
      <div className="admin-centre">
        <ErrorNotice error={admin} onRetry={() => setAttempt((count) => count + 1)} />
      </div>
    );
  // two-step login comes before anything else: set it up once, then enter a code for each new session
  const signOut = () => void session.signOut();
  const reload = () => setAttempt((count) => count + 1);
  if (admin.twoStep.status === 'setup_required')
    return (
      <div className="admin-centre">
        <TwoStepSetup onDone={reload} onSignOut={signOut} />
      </div>
    );
  if (admin.twoStep.status === 'verify_required')
    return (
      <div className="admin-centre">
        <TwoStepVerify onDone={reload} onSignOut={signOut} />
      </div>
    );
  return (
    <>
      <Dashboard reply={admin} onSignOut={signOut} />
      <TwoStepPrompt />
    </>
  );
}

function Dashboard({ reply, onSignOut }: { reply: AdminSessionReply; onSignOut: () => void }) {
  const { admin, twoStep } = reply;
  const { pathname } = useLocation();
  const value = useMemo(
    () => ({
      admin,
      twoStep,
      can: (permission: string) => admin.superAdmin || admin.permissions.includes(permission),
    }),
    [admin, twoStep],
  );
  const routes = pagesFor(value.can);
  return (
    <AdminContext.Provider value={value}>
      <AdminShell
        title="London Life Admin"
        environment={env.environment}
        user={{
          name: admin.user.displayName ?? admin.user.email ?? 'Admin',
          detail: admin.superAdmin ? 'Super admin' : admin.roles.map((role) => role.name).join(', ') || 'Admin',
        }}
        groups={GROUPS}
        pathname={pathname}
        can={value.can}
        Link={NavLink}
        breadcrumbs={crumbsFor(pathname)}
        onSignOut={onSignOut}
      >
        <Routes>
          {routes.map((item) => (
            <Route key={item.path} path={item.path} element={item.element} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AdminShell>
    </AdminContext.Provider>
  );
}
